/* eslint sort-keys: "error" */

import { Text } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';
import { Link } from '@vimeo/iris/typography';

export const guestTranslations = {
  accessDenied: translate('Access denied'),
  allSpeakersAreOnStage: translate('All speakers are on stage'),
  allUnmutedAreRecorded: translate('All unmuted speakers are recorded'),
  anonymous: translate('Anonymous'),
  audience: translate('Audience'),
  audienceCantHearUnmuted: translate('The audience cannot hear anyone backstage'),
  audienceHearsUnmuted: translate('The audience hears all unmuted speakers'),
  backStage: translate('Backstage'),
  backstageAreNotRecorded: translate('Backstage speakers are not recorded'),
  backstageCount: (count: number) =>
    translate({
      count,
      plural: 'There are {COUNT} other speakers backstage',
      replacements: {
        COUNT: count,
      },
      singular: 'There is {COUNT} other speaker backstage',
    }),
  cameraMicBlocked: (url: string) =>
    translate({
      replacements: {
        HELP_CENTER: (children: string) => (
          <Link target={'_blank'} href={url} format={'soft'}>
            {children}
          </Link>
        ),
      },
      singular:
        'Your camera and mic are blocked. To learn how to enable them, read more {HELP_CENTER}here{/HELP_CENTER}.',
    }),
  chatAskHostEnable: translate(
    'Ask the event host to enable chat to send messages to your audience',
  ),
  close: translate('Close'),
  connectionQualityGood: translate('Your stream connection looks good'),
  connectionQualityReduced: translate(
    'Your stream quality is reduced. Please check your internet connection.',
  ),
  enterBackstage: translate('Enter backstage'),
  eventLive: translate('Event Live'),
  eventStarted: translate('This event has started'),
  exitFullscreen: translate('Exit fullscreen'),
  fullscreen: translate('Fullscreen'),
  getStartedEnableCam: translate('To get started, enable your camera and microphone'),
  goToHomepage: translate('Go to homepage'),
  guestStatusAboutLive: translate('You are about to go live!'),
  guestStatusBackstage: translate('You are backstage and muted'),
  guestStatusLive: translate('You’re live!'),
  guestStatusMessageJoining: translate(
    'Check your camera and sound before heading backstage. You’ll join with your microphone muted.',
  ),
  hideChat: translate('Hide chat'),
  joinLiveFormConfirmation: translate({
    replacements: {
      PRIVACY_LINK: (children: string) => (
        <Text
          variant="body-xl"
          target={'_blank'}
          href={'/privacy'}
          key={'guest-privacy-link'}
          color={'text-secondary'}
          fontSize={'text-sm'}
          textDecoration={'underline'}
          as={'a'}
        >
          {children}
        </Text>
      ),
      TERMS_LINK: (children: string) => (
        <Text
          variant="body-xl"
          target={'_blank'}
          href={'/terms'}
          key={'guest-terms-link'}
          color={'text-secondary'}
          fontSize={'text-sm'}
          textDecoration={'underline'}
          as={'a'}
        >
          {children}
        </Text>
      ),
    },
    singular:
      'By checking this box, you consent to being recorded and agree to Vimeo’s {TERMS_LINK}Terms of Service{/TERMS_LINK} and {PRIVACY_LINK}Privacy Policy{/PRIVACY_LINK}',
  }),
  joinLiveFormTermsAndPrivacy: translate({
    replacements: {
      PRIVACY_LINK: (children: string) => (
        <Text
          target={'_blank'}
          href={'/privacy'}
          key={'guest-privacy-link'}
          variant={'body-xs'}
          color={'text-secondary'}
          textDecoration={'underline'}
          as={'a'}
        >
          {children}
        </Text>
      ),
      TERMS_LINK: (children: string) => (
        <Text
          target={'_blank'}
          href={'/terms'}
          key={'guest-terms-link'}
          variant={'body-xs'}
          color={'text-secondary'}
          textDecoration={'underline'}
          as={'a'}
        >
          {children}
        </Text>
      ),
    },
    singular:
      'By entering this event, you agree to Vimeo’s {TERMS_LINK}Terms of Service{/TERMS_LINK} and acknowledge Vimeo’s {PRIVACY_LINK}Privacy Policy{/PRIVACY_LINK}.',
  }),
  joinNow: translate('Join now'),
  newGuestPageChatTourPointText: translate(
    'View and interact with chat and Q&A while in full screen mode',
  ),
  newGuestPageChatTourPointTitle: translate('Chat'),
  next: translate('Next'),
  noMoreScenes: translate('No more scenes'),
  offline: translate('offline'),
  onAir: translate('ON AIR'),
  showChat: translate('Show chat'),
  stage: translate('Stage'),
  startsInDayHour: (days: number, hours: number) =>
    translate({
      replacements: {
        DAY: days,
        HOUR: hours,
      },
      singular: 'Starts in {DAY} day, {HOUR} hour',
    }),
  startsInDayHours: (days: number, hours: number) =>
    translate({
      count: days,
      plural: 'Starts in {DAY} days, {HOUR} hours',
      replacements: {
        DAY: days,
        HOUR: hours,
      },
      singular: 'Starts in {DAY} day, {HOUR} hours',
    }),
  startsInDaysHour: (days: number, hours: number) =>
    translate({
      count: hours,
      plural: 'Starts in {DAY} days, {HOUR} hours',
      replacements: {
        DAY: days,
        HOUR: hours,
      },
      singular: 'Starts in {DAY} days, {HOUR} hour',
    }),
  startsInHourMinute: (hours: number, minutes: number) =>
    translate({
      replacements: {
        HOUR: hours,
        MINUTE: minutes,
      },
      singular: 'Starts in {HOUR} hour, {MINUTE} minute',
    }),
  startsInHourMinutes: (hours: number, minutes: number) =>
    translate({
      count: hours,
      plural: 'Starts in {HOUR} hours, {MINUTE} minutes',
      replacements: {
        HOUR: hours,
        MINUTE: minutes,
      },
      singular: 'Starts in {HOUR} hour, {MINUTE} minutes',
    }),
  startsInHoursMinute: (hours: number, minutes: number) =>
    translate({
      count: minutes,
      plural: 'Starts in {HOUR} hours, {MINUTE} minutes',
      replacements: {
        HOUR: hours,
        MINUTE: minutes,
      },
      singular: 'Starts in {HOUR} hours, {MINUTE} minute',
    }),
  startsInMinutes: (count: number) =>
    translate({
      count,
      plural: 'Starts in {MINUTE} minutes',
      replacements: { MINUTE: count },
      singular: 'Starts in {MINUTE} minute',
    }),
  thanksForJoining: translate('Thanks for joining!'),
  unmute: translate('Unmute'),
  upNext: translate('Up next'),
  welcomeName: (name: string) =>
    translate({
      replacements: {
        NAME: name,
      },
      singular: 'Welcome, {NAME}',
    }),
  youDontHaveAccessToThisEvent: translate("You don't have access to this event"),
  youNoLongerHaveAccess: translate('You no longer have access to this'),
  yourEventEnded: translate('Your event has ended'),
  youreUpNext: translate('You’re up next'),
};
